import { Space } from 'antd'
import { motion } from 'framer-motion'
import { useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useCopyToClipboard } from 'react-use'

import { API } from '@/api'
import AddIcon from '@/assets/icons/add.svg?react'
import CopyIcon from '@/assets/icons/copy.svg?react'
import DeleteIcon from '@/assets/icons/delete.svg?react'
import EditIcon from '@/assets/icons/pencil.svg?react'
import WindowOpenIcon from '@/assets/icons/window-open.svg?react'
import { Divider, EmptyView, Loading, message, Trans } from '@/components'
import { PortProtocol, portProtocolLabel } from '@/consts/machine'
import { TeamAppContext } from '@/contexts/TeamContext'
import { Instance, Port, SSHAuth } from '@/models/instance'
import { RemovePortModal } from '@/pages/instance-list/components/RemovePortModal.tsx'
import { useUserStore } from '@/stores/user'
import { cn } from '@/utils/style'

import { PortModal } from './PortModal'
import { PublicIPPortModal } from './PublicIPPortModal'

interface InstanceAccessProps {
  instance: Instance
  onUpdate?: () => void
  bgClassName?: string
  readOnly?: boolean
}

const getHostFromLink = (link: string) => {
  return link.replace(/^(.*:\/\/)/, '')
}

const getSSHCommand = (link: string, user: string) => {
  const hostPort = getHostFromLink(link).split(':') // trim 'ssh://'
  const ip = hostPort[0]
  const port = hostPort[1]

  return `ssh -p ${port} ${user}@${ip}`
}

interface PortActionProps {
  onEdit: () => void
  onDelete: () => void
  showNewPortForwarding?: boolean
  showDelete?: boolean
  showEdit?: boolean
  onNewPortForwarding?: () => void
  isPublicIPPort?: boolean
  readOnly?: boolean
}

function PortAction({
  onEdit,
  onDelete,
  showNewPortForwarding = true,
  showDelete = false,
  showEdit = false,
  onNewPortForwarding,
  isPublicIPPort,
  readOnly = false,
}: PortActionProps) {
  return (
    <div className="px-4 pb-4">
      <Divider className="mb-4" />
      <div className="flex gap-4 items-center">
        {!readOnly && showNewPortForwarding && (
          <div className="flex items-center gap-1.5">
            <AddIcon className="w-4 h-4 text-btn-secondary" />
            <div className="text-btn-secondary cursor-pointer" onClick={onNewPortForwarding}>
              <Trans
                i18nKey={
                  isPublicIPPort
                    ? 'instance.access.portAction.newPortBinding'
                    : 'instance.access.portAction.newPortForwarding'
                }
              >
                {isPublicIPPort ? 'New Port Binding' : 'New Port Forwarding'}
              </Trans>
            </div>
          </div>
        )}
        {!readOnly && showEdit && (
          <div className="flex items-center gap-1.5">
            <EditIcon className="w-4 h-4 text-btn-secondary" />
            <div className="text-btn-secondary cursor-pointer" onClick={onEdit}>
              <Trans i18nKey="instance.access.portAction.editThisPort">Edit This Port</Trans>
            </div>
          </div>
        )}
        {!readOnly && showDelete && (
          <div className="flex items-center gap-1.5">
            <DeleteIcon className="w-4 h-4 text-red-500" />
            <div className="text-red-500 cursor-pointer" onClick={onDelete}>
              <Trans i18nKey="instance.access.portAction.deleteThisPort">Delete This Port</Trans>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

function SSHInfo({
  port,
  auth,
  onEdit,
  onDelete,
  onNewPortForwarding,
  isPublicIPPort,
  readOnly,
  showDelete = false,
  showEdit = false,
}: {
  port: Port
  auth: SSHAuth
  onEdit: () => void
  onDelete: () => void
  onNewPortForwarding?: () => void
  isPublicIPPort?: boolean
  readOnly?: boolean
  showDelete?: boolean
  showEdit?: boolean
}) {
  const [state, copyToClipboard] = useCopyToClipboard()
  const sshPort = getHostFromLink(port.url).split(':')[1]
  const navigate = useNavigate()
  const userStore = useUserStore()
  const { t } = useTranslation()
  const teamApp = useContext(TeamAppContext)
  const isTeamContext = teamApp !== null

  useEffect(() => {
    if (state.value) {
      message.success(t('instance.access.message.copied'))
    }
  }, [state, t])

  return (
    <div>
      {port.srcPort === 22 && (
        <>
          <div className="grid grid-cols-3 gap-y-4 p-4">
            <div className="space-y-1">
              <div className="t-xs text-secondary">
                <Trans i18nKey="instance.access.ssh.command">SSH Command</Trans>
              </div>
              <div className="t-sb flex items-center gap-x-1">
                {getSSHCommand(port.url, auth.user)}
                <CopyIcon
                  className={cn(
                    'w-4 h-4 text-btn-secondary inline-block',
                    port.url ? 'cursor-pointer' : 'opacity-50 cursor-not-allowed',
                  )}
                  onClick={() => {
                    if (port.url) {
                      copyToClipboard(getSSHCommand(port.url, auth.user))
                    }
                  }}
                />
              </div>
            </div>
            <div className="space-y-1">
              <div className="t-xs text-secondary">
                <Trans i18nKey="instance.access.ssh.servicePort">Service Port</Trans>
              </div>
              <div className="t-sb flex items-center gap-x-1">
                {sshPort}
                <CopyIcon
                  className={cn(
                    'w-4 h-4 text-btn-secondary inline-block',
                    sshPort ? 'cursor-pointer' : 'opacity-50 cursor-not-allowed',
                  )}
                  onClick={() => {
                    if (sshPort) {
                      copyToClipboard(sshPort)
                    }
                  }}
                />
              </div>
            </div>
            <div className="space-y-1">
              <div className="t-xs text-secondary">
                <Trans i18nKey="instance.access.ssh.sshKey">SSH Key</Trans>
              </div>
              <div className="t-sb flex items-center gap-x-1">
                {userStore.sshPubKey.length > 0 ? (
                  <div className="t-sb text-success">
                    <Trans i18nKey="instance.access.ssh.active">Active</Trans>
                  </div>
                ) : (
                  <div className="t-sb text-tertiary">
                    <Trans i18nKey="instance.access.ssh.inactive">Inactive</Trans>
                  </div>
                )}
                <EditIcon
                  className={cn(
                    'w-4 h-4 text-btn-secondary inline-block',
                    sshPort ? 'cursor-pointer' : 'opacity-50 cursor-not-allowed',
                  )}
                  onClick={() => {
                    navigate(isTeamContext ? `/team/profile` : `/profile`)
                  }}
                />
              </div>
            </div>
            <div className="space-y-1">
              <div className="t-xs text-secondary">
                <Trans i18nKey="instance.access.ssh.user">User</Trans>
              </div>
              <div className="t-sb flex items-center gap-x-1">
                {auth.user}
                <CopyIcon
                  className={cn(
                    'w-4 h-4 text-btn-secondary inline-block',
                    auth.user ? 'cursor-pointer' : 'opacity-50 cursor-not-allowed',
                  )}
                  onClick={() => {
                    if (auth.user) {
                      copyToClipboard(auth.user)
                    }
                  }}
                />
              </div>
            </div>
            <div className="space-y-1">
              <div className="t-xs text-secondary">
                <Trans i18nKey="instance.access.ssh.password">Password</Trans>
              </div>
              <div className="t-sb flex items-center gap-x-1">
                ********
                <CopyIcon
                  className={cn(
                    'w-4 h-4 text-btn-secondary inline-block',
                    auth.password ? 'cursor-pointer' : 'opacity-50 cursor-not-allowed',
                  )}
                  onClick={() => {
                    if (auth.password) {
                      copyToClipboard(auth.password)
                    }
                  }}
                />
              </div>
            </div>
          </div>
          <PortAction
            onEdit={onEdit}
            onDelete={onDelete}
            showNewPortForwarding={true}
            showDelete={showDelete}
            showEdit={showEdit}
            onNewPortForwarding={onNewPortForwarding}
            isPublicIPPort={isPublicIPPort}
            readOnly={readOnly}
          />
        </>
      )}
      {port.srcPort !== 22 && (
        <>
          <div className="grid grid-cols-2 gap-y-4 p-4">
            <div className="space-y-1">
              <div className="t-xs text-secondary">
                <Trans i18nKey="instance.access.tcp.hostname">Hostname</Trans>
              </div>
              <div className="t-sb flex items-center gap-x-1">
                {getHostFromLink(port.url).split(':')[0]}
                <CopyIcon
                  className={cn(
                    'w-4 h-4 text-btn-secondary inline-block',
                    port.url ? 'cursor-pointer' : 'opacity-50 cursor-not-allowed',
                  )}
                  onClick={() => {
                    if (port.url) {
                      copyToClipboard(getHostFromLink(port.url).split(':')[0])
                    }
                  }}
                />
              </div>
            </div>
            <div className="space-y-1">
              <div className="t-xs text-secondary">
                <Trans i18nKey="instance.access.tcp.port">Port</Trans>
              </div>
              <div className="t-sb flex items-center gap-x-1">
                {getHostFromLink(port.url).split(':')[1]}
                <CopyIcon
                  className={cn(
                    'w-4 h-4 text-btn-secondary inline-block',
                    port.url ? 'cursor-pointer' : 'opacity-50 cursor-not-allowed',
                  )}
                  onClick={() => {
                    if (port.url) {
                      copyToClipboard(getHostFromLink(port.url).split(':')[1])
                    }
                  }}
                />
              </div>
            </div>
          </div>
          <PortAction
            onEdit={onEdit}
            onDelete={onDelete}
            showNewPortForwarding={true}
            showDelete={showDelete}
            showEdit={showEdit}
            onNewPortForwarding={onNewPortForwarding}
            isPublicIPPort={isPublicIPPort}
            readOnly={readOnly}
          />
        </>
      )}
    </div>
  )
}

function HttpInfo({ port }: { port: Port }) {
  const [state, copyToClipboard] = useCopyToClipboard()
  const { t } = useTranslation()

  useEffect(() => {
    if (state.value) {
      message.success(t('instance.access.message.copied'))
    }
  }, [state, t])

  return (
    <div className="py-4 flex items-center t-sm">
      <div className="w-[254px] pl-4">
        <div className="t-sm text-secondary">
          <Trans i18nKey="instance.access.http.link">Link</Trans>
        </div>
        <Space>
          <div
            className={cn(
              'flex items-center gap-1 text-btn-secondary',
              port.url ? 'cursor-pointer' : 'opacity-50 cursor-not-allowed',
            )}
            onClick={() => {
              if (port.url) {
                window.open(port.url, '_blank')
              }
            }}
          >
            <WindowOpenIcon className="w-4 h-4" />
            <Trans i18nKey="instance.access.http.open">Open</Trans>
          </div>
          <div
            className={cn(
              'flex items-center gap-1 text-btn-secondary',
              port.url ? 'cursor-pointer' : 'opacity-50 cursor-not-allowed',
            )}
            onClick={() => {
              if (port.url) {
                copyToClipboard(port.url)
              }
            }}
          >
            <CopyIcon className="w-4 h-4" />
            <Trans i18nKey="instance.access.http.copy">Copy</Trans>
          </div>
        </Space>
      </div>
    </div>
  )
}

function VNCInfo({ port, auth }: { port: Port; auth: SSHAuth }) {
  const [state, copyToClipboard] = useCopyToClipboard()
  const vncPort = getHostFromLink(port.url).split(':')[1]
  const { t } = useTranslation()

  useEffect(() => {
    if (state.value) {
      message.success(t('instance.access.message.copied'))
    }
  }, [state, t])

  return (
    <div className="grid grid-cols-2 gap-y-4 p-4">
      <div className="space-y-1">
        <div className="t-xs text-secondary">
          <Trans i18nKey="instance.access.vnc.command">VNC Command</Trans>
        </div>
        <div className="t-sb flex items-center gap-x-1">
          {port.url}
          <CopyIcon
            className={cn(
              'w-4 h-4 text-btn-secondary inline-block',
              port.url ? 'cursor-pointer' : 'opacity-50 cursor-not-allowed',
            )}
            onClick={() => {
              if (port.url) {
                copyToClipboard(port.url)
              }
            }}
          />
        </div>
      </div>
      <div className="space-y-1">
        <div className="t-xs text-secondary">
          <Trans i18nKey="instance.access.vnc.servicePort">Service Port</Trans>
        </div>
        <div className="t-sb flex items-center gap-x-1">
          {vncPort}
          <CopyIcon
            className={cn(
              'w-4 h-4 text-btn-secondary inline-block',
              vncPort ? 'cursor-pointer' : 'opacity-50 cursor-not-allowed',
            )}
            onClick={() => {
              if (vncPort) {
                copyToClipboard(vncPort)
              }
            }}
          />
        </div>
      </div>
      <div className="space-y-1">
        <div className="t-xs text-secondary">
          <Trans i18nKey="instance.access.vnc.user">User</Trans>
        </div>
        <div className="t-sb flex items-center gap-x-1">
          {auth.user}
          <CopyIcon
            className={cn(
              'w-4 h-4 text-btn-secondary inline-block',
              auth.user ? 'cursor-pointer' : 'opacity-50 cursor-not-allowed',
            )}
            onClick={() => {
              if (auth.user) {
                copyToClipboard(auth.user)
              }
            }}
          />
        </div>
      </div>
      <div className="space-y-1">
        <div className="t-xs text-secondary">
          <Trans i18nKey="instance.access.vnc.password">Password</Trans>
        </div>
        <div className="t-sb flex items-center gap-x-1">
          ********
          <CopyIcon
            className={cn(
              'w-4 h-4 text-btn-secondary inline-block',
              auth.password ? 'cursor-pointer' : 'opacity-50 cursor-not-allowed',
            )}
            onClick={() => {
              if (auth.password) {
                copyToClipboard(auth.password)
              }
            }}
          />
        </div>
      </div>
    </div>
  )
}

function RDPInfo({ port, auth }: { port: Port; auth: SSHAuth }) {
  const [state, copyToClipboard] = useCopyToClipboard()
  const rdpPort = getHostFromLink(port.url).split(':')[1]
  const { t } = useTranslation()

  useEffect(() => {
    if (state.value) {
      message.success(t('instance.access.message.copied'))
    }
  }, [state, t])

  return (
    <div className="grid grid-cols-2 gap-y-4 p-4">
      <div className="space-y-1">
        <div className="t-xs text-secondary">
          <Trans i18nKey="instance.access.rdp.command">RDP Command</Trans>
        </div>
        <div className="t-sb flex items-center gap-x-1">
          {port.url}
          <CopyIcon
            className={cn(
              'w-4 h-4 text-btn-secondary inline-block',
              port.url ? 'cursor-pointer' : 'opacity-50 cursor-not-allowed',
            )}
            onClick={() => {
              if (port.url) {
                copyToClipboard(port.url)
              }
            }}
          />
        </div>
      </div>
      <div className="space-y-1">
        <div className="t-xs text-secondary">
          <Trans i18nKey="instance.access.rdp.servicePort">Service Port</Trans>
        </div>
        <div className="t-sb flex items-center gap-x-1">
          {rdpPort}
          <CopyIcon
            className={cn(
              'w-4 h-4 text-btn-secondary inline-block',
              rdpPort ? 'cursor-pointer' : 'opacity-50 cursor-not-allowed',
            )}
            onClick={() => {
              if (rdpPort) {
                copyToClipboard(rdpPort)
              }
            }}
          />
        </div>
      </div>
      <div className="space-y-1">
        <div className="t-xs text-secondary">
          <Trans i18nKey="instance.access.rdp.user">User</Trans>
        </div>
        <div className="t-sb flex items-center gap-x-1">
          {auth.user}
          <CopyIcon
            className={cn(
              'w-4 h-4 text-btn-secondary inline-block',
              auth.user ? 'cursor-pointer' : 'opacity-50 cursor-not-allowed',
            )}
            onClick={() => {
              if (auth.user) {
                copyToClipboard(auth.user)
              }
            }}
          />
        </div>
      </div>
      <div className="space-y-1">
        <div className="t-xs text-secondary">
          <Trans i18nKey="instance.access.rdp.password">Password</Trans>
        </div>
        <div className="t-sb flex items-center gap-x-1">
          ********
          <CopyIcon
            className={cn(
              'w-4 h-4 text-btn-secondary inline-block',
              auth.password ? 'cursor-pointer' : 'opacity-50 cursor-not-allowed',
            )}
            onClick={() => {
              if (auth.password) {
                copyToClipboard(auth.password)
              }
            }}
          />
        </div>
      </div>
    </div>
  )
}

export function InstanceAccess({
  instance,
  onUpdate,
  bgClassName,
  readOnly = false,
}: InstanceAccessProps) {
  const [active, setActive] = useState(0)
  const [loading, setLoading] = useState(false)
  const [openPortModal, setOpenPortModal] = useState<number>(0)
  const [openRemovePortModal, setOpenRemovePortModal] = useState<boolean>(false)
  const { t } = useTranslation()

  useEffect(() => {
    if (instance.ports?.length > 0 && active >= instance.ports.length) {
      setActive(0)
    }
  }, [instance.ports?.length, active])

  const onDelete = (portIdx: number) => {
    const port = instance.ports?.[portIdx]
    if (!port) {
      return
    }
    setLoading(true)
    const destPort = isIPBinding
      ? instance.nats![0].portMappings.items.find((item) => item.targetPort === port.srcPort)
          ?.listenPort
      : port.destPort

    API.updateInstancePort({
      request_id: instance.displayID,
      type: 2, // remove port
      add_port_op: {
        ports: JSON.stringify([]),
      },
      remove_port_op: {
        ports: JSON.stringify([{ ...port, destPort }]),
      },
    })
      .then((res) => {
        if (res.code === 0) {
          message.success(t('instance.access.message.taskSubmitted'))
          onUpdate?.()
        }
      })
      .finally(() => {
        setLoading(false)
      })
  }

  const isIPBinding = instance.nats && instance.nats.length > 0

  const defaultSrcPorts = new Set(instance.node?.image?.ports?.map((p) => p.srcPort) ?? [])

  const isTCP = (port: Port) => port.protocol === PortProtocol.SSH && port.srcPort !== 22

  return (
    <div className={cn('rounded-lg', bgClassName ?? 'bg-panel-gray-100')}>
      {loading ? (
        <div className="w-full flex items-center justify-center p-8">
          <Loading />
        </div>
      ) : (
        <div>
          {!instance.ports || instance.ports.length === 0 ? (
            <EmptyView
              title={t('instance.list.noPortsConfiguredTitle')}
              content={t('instance.list.noPortsConfiguredContent')}
              action={
                !readOnly ? (
                  <div
                    className="flex items-center gap-1.5 cursor-pointer"
                    onClick={() => setOpenPortModal(2)}
                  >
                    <AddIcon className="w-4 h-4 text-btn-secondary" />
                    <div className="text-btn-secondary">
                      {isIPBinding
                        ? t('instance.list.newPortBinding')
                        : t('instance.list.newPortForwarding')}
                    </div>
                  </div>
                ) : undefined
              }
            />
          ) : (
            <>
              <div className="flex flex-grow items-center pt-4 border-b border-gray100">
                {instance.ports?.map((port, idx) => (
                  <div className="px-4" onClick={() => setActive(idx)} key={idx}>
                    <div className="pb-3 relative cursor-pointer">
                      {isTCP(port) ? 'TCP' : portProtocolLabel[port.protocol]} Port {port.srcPort}
                      {active === idx && (
                        <motion.div
                          className="absolute left-0 right-0 bottom-0 h-[3px] bg-btn-secondary"
                          layoutId={`instance-${instance.displayID}-tab`}
                        />
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <>
                {instance.ports?.map((port, idx) => {
                  if (port.protocol === PortProtocol.SSH && active === idx) {
                    return (
                      <div key={idx}>
                        <SSHInfo
                          port={port}
                          auth={instance.auth}
                          key={idx}
                          onEdit={() => setOpenPortModal(1)}
                          onDelete={() => setOpenRemovePortModal(true)}
                          onNewPortForwarding={() => setOpenPortModal(2)}
                          isPublicIPPort={isIPBinding}
                          readOnly={readOnly}
                          showDelete={!isIPBinding && !defaultSrcPorts.has(port.srcPort)}
                          showEdit={isIPBinding}
                        />
                      </div>
                    )
                  }

                  if (port.protocol === PortProtocol.HTTP && active === idx) {
                    return (
                      <div key={idx}>
                        <HttpInfo port={port} />
                        <PortAction
                          onEdit={() => setOpenPortModal(1)}
                          onDelete={() => setOpenRemovePortModal(true)}
                          onNewPortForwarding={() => setOpenPortModal(2)}
                          showDelete={!isIPBinding && !defaultSrcPorts.has(port.srcPort)}
                          showEdit={isIPBinding && !defaultSrcPorts.has(port.srcPort)}
                          showNewPortForwarding={(instance.ports?.length || 0) < 10}
                          isPublicIPPort={isIPBinding}
                          readOnly={readOnly}
                        />
                      </div>
                    )
                  }

                  if (port.protocol === PortProtocol.VNC && active === idx) {
                    return (
                      <div key={idx}>
                        <VNCInfo port={port} auth={instance.auth} key={idx} />
                        <PortAction
                          onEdit={() => setOpenPortModal(1)}
                          onDelete={() => setOpenRemovePortModal(true)}
                          onNewPortForwarding={() => setOpenPortModal(2)}
                          showDelete={false}
                          showEdit={isIPBinding && !defaultSrcPorts.has(port.srcPort)}
                          showNewPortForwarding={(instance.ports?.length || 0) < 10}
                          isPublicIPPort={isIPBinding}
                          readOnly={readOnly}
                        />
                      </div>
                    )
                  }

                  if (port.protocol === PortProtocol.RDP && active === idx) {
                    return (
                      <div key={idx}>
                        <RDPInfo port={port} auth={instance.auth} key={idx} />
                        <PortAction
                          onEdit={() => setOpenPortModal(1)}
                          onDelete={() => setOpenRemovePortModal(true)}
                          onNewPortForwarding={() => setOpenPortModal(2)}
                          showDelete={false}
                          showEdit={isIPBinding && !defaultSrcPorts.has(port.srcPort)}
                          showNewPortForwarding={(instance.ports?.length || 0) < 10}
                          isPublicIPPort={isIPBinding}
                          readOnly={readOnly}
                        />
                      </div>
                    )
                  }
                })}
              </>
            </>
          )}

          {openPortModal && isIPBinding ? (
            <PublicIPPortModal
              open={openPortModal > 0}
              onClose={() => setOpenPortModal(0)}
              instance={instance}
              port={instance.ports?.[active]}
              mode={openPortModal}
              onUpdate={onUpdate}
            />
          ) : null}

          {openPortModal && !isIPBinding ? (
            <PortModal
              open={openPortModal > 0}
              onClose={() => setOpenPortModal(0)}
              instance={instance}
              port={instance.ports?.[active]}
              mode={openPortModal}
              onUpdate={onUpdate}
            />
          ) : null}

          {openRemovePortModal ? (
            <RemovePortModal
              open={openRemovePortModal}
              onClose={() => setOpenRemovePortModal(false)}
              onOk={() => onDelete(active)}
            />
          ) : null}
        </div>
      )}
    </div>
  )
}
